/**
 * Types for Jyotish chart application
 */

// Basic coordinate type (degrees, minutes, seconds)
export interface Coordinate {
    degrees: number;
    minutes: number;
    seconds: number;
}

// Format for displaying coordinates
export function formatCoordinate(coord: Coordinate): string {
    return `${coord.degrees}°${coord.minutes}'${coord.seconds}"`;
}

// Rasi (zodiac sign) types
export enum RasiName {
    Mesha = "Mesha",
    Vrishabha = "Vrishabha",
    Mithuna = "Mithuna",
    Karka = "Karka",
    Simha = "Simha",
    Kanya = "Kanya",
    Tula = "Tula",
    Vrishchika = "Vrishchika",
    Dhanu = "Dhanu",
    Makara = "Makara",
    Kumbha = "Kumbha",
    Meena = "Meena"
}

// Planet types - 100% Sanskrit names (Bhrigu Parampara)
export enum GrahaName {
    Surya = "Sy", // Surya
    Sy = "Sy", // Surya
    Chandra = "Ch", // Chandra
    Ch = "Ch",
    Mangala = "Ma", // Mangala
    Ma = "Ma",
    Budha = "Bu", // Budha
    Bu = "Bu",
    Guru = "Gu", // Guru
    Gu = "Gu",
    Sukra = "Sk", // Sukra
    Sk = "Sk",
    Shani = "Sa", // Shani
    Sa = "Sa",
    Rahu = "Ra",
    Ketu = "Ke",
    Lg = "Lg" // Lagna (Ascendant)
}

// Nakshatra (lunar mansion) types
export enum NakshatraName {
    Ashwini = "Ashwini",
    Bharani = "Bharani",
    Krittika = "Krittika",
    Rohini = "Rohini",
    Mrigashirsha = "Mrigashirsha",
    Ardra = "Ardra",
    Punarvasu = "Punarvasu",
    Pushya = "Pushya",
    Ashlesha = "Ashlesha",
    Magha = "Magha",
    PurvaPhalguni = "Purva Phalguni",
    UttaraPhalguni = "Uttara Phalguni",
    Hasta = "Hasta",
    Chitra = "Chitra",
    Swati = "Swati",
    Vishakha = "Vishakha",
    Anuradha = "Anuradha",
    Jyeshtha = "Jyeshtha",
    Mula = "Mula",
    PurvaAshadha = "Purva Ashadha",
    UttaraAshadha = "Uttara Ashadha",
    Shravana = "Shravana",
    Dhanishtha = "Dhanishtha",
    Shatabhisha = "Shatabhisha",
    PurvaBhadrapada = "Purva Bhadrapada",
    UttaraBhadrapada = "Uttara Bhadrapada",
    Revati = "Revati"
}

// Graha position information
export interface GrahaPosition {
    graha: GrahaName;
    position: Coordinate;
    /** УНИФИЦИРОВАННАЯ ПОЗИЦИЯ в формате "rasi|degrees|longitude|speed" для анимации */
    positionString?: string;
    rasi: RasiName;
    nakshatra?: NakshatraName;
    isRetrograde?: boolean;
    metadata?: {
        [key: string]: any;
    };
    // API response data for enhanced precision
    apiResponse?: {
        grahaPositions?: any[];
    };
}

// Chart data representation
export interface ChartData {
    grahaPositions: GrahaPosition[];
    // Other chart properties can be added here
}

// Import-related types
export interface ImportedPlanetData {
    name: string;
    longitude: string;
    nakshatra: string;
    pada: number;
    rasi: string;
    navamsa: string;
    isRetrograde: boolean;
}

export interface ParsedChartData {
    planets: GrahaPosition[]; // Включает все планеты + Лагну в едином массиве
    hora?: {
        graha: GrahaName | null;
        lord: string;
    };
    muhurtaGrahas?: string[]; // Array of 30 muhurta graha labels for chakra rendering
    centerDMS?: {
        degrees: number;
        minutes: number;
        seconds: number;
    }; // Center coordinates for muhurta sectors
    parseErrors: string[];
    originalData: string;
    chartDateTime?: import('./storage').ChartDateTime; // Optional extracted date/time info
    sunriseSuryaLongitude?: string; // Sunrise Surya longitude in DMS format (e.g., "112° 09' 31.27"")

    // Full API response data (optional) - preserves all information from server
    apiResponse?: import('../types/JatakaApiTypes').JatakaApiResponse;

    // Detailed panchanga data from API (required for chakra rendering)
    detailed_panchanga?: any;
}

export interface ImportResult {
    success: boolean;
    data?: ParsedChartData;
    error?: string;
}
